import { Trans, useTranslation } from 'next-i18next'
import { Control, FieldValues, FormState } from 'react-hook-form'
import styled from 'styled-components'
import { Checkbox } from '@fe/common/components/inputs/Checkbox'
import { Dropdown } from '@fe/common/components/inputs/Dropdown'
import { Input } from '@fe/common/components/inputs/Input'
import { COLOR, FONT_SIZE } from '@fe/common/constants/main'
import { Errors } from '@fe/common/types/Errors'
import yup from '@fe/common/yup/yupConfig'
import {
  FINTWIN_DOCS,
  SIGNUP_SOURCE_CODES,
  SIGNUP_SOURCES_WITH_DETAIL,
  SignupSourceCode,
} from 'src/constants/fintwinLegal'

const Container = styled.div`
  grid-column: 1/3;
  margin-top: 1rem;
`

const Consents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  /* Bottom spacing comes from FormStep's AgreementsContainer that follows. */
  margin-top: 1rem;
  font-size: ${FONT_SIZE.SMALL};

  /* Long legal labels wrap as running text; the shared LabelText is
     inline-flex, which would lay the inline links out as separate columns. */
  label > span {
    display: inline;
  }

  a {
    margin: 0;
  }
`

export const LegalNotice = styled.p`
  grid-column: 1/3;
  max-width: 60rem;
  margin-top: 2rem;
  color: ${COLOR.GREY};
  font-size: ${FONT_SIZE.EXTRA_SMALL};
  line-height: 1.5;
`

export interface FintwinSignupData {
  source_code: SignupSourceCode
  source_detail?: string
  consent_terms: boolean
  attestation_own_initiative: boolean
  attestation_canada_notice: boolean
}

export const isDetailRequired = (code?: SignupSourceCode) =>
  !!code && SIGNUP_SOURCES_WITH_DETAIL.includes(code)

const requiredConsent = () =>
  yup.boolean().oneOf([true], 'mixed.required').required()

export const fintwinSignupShape = {
  source_code: yup
    .string()
    .oneOf([...SIGNUP_SOURCE_CODES])
    .required(),
  source_detail: yup
    .string()
    .trim()
    .max(255)
    .when('source_code', {
      is: (code: SignupSourceCode) => isDetailRequired(code),
      then: yup.string().trim().max(255).required(),
    }),
  consent_terms: requiredConsent(),
  attestation_own_initiative: requiredConsent(),
  attestation_canada_notice: requiredConsent(),
}

interface FintwinSignupConsentsProps {
  register: React.Ref<HTMLInputElement>
  control: Control
  errors: Errors
  formState: FormState<FieldValues>
  sourceCode?: SignupSourceCode
}

/**
 * FINTWIN-only block of the signup form: the "how did you hear about us"
 * question and the three mandatory confirmations. Follows FormStep's manual
 * props form style (no FormProvider there), so the form wiring is passed in.
 */
export const FintwinSignupConsents: React.FC<FintwinSignupConsentsProps> = ({
  register,
  control,
  errors,
  formState,
  sourceCode,
}) => {
  const { t } = useTranslation('no-auth')

  const sourceOptions = SIGNUP_SOURCE_CODES.map(code => ({
    value: code,
    label: t(`signup-source-${code}`),
  }))

  return (
    <Container>
      <Dropdown
        name="source_code"
        label={t('signup-source-label')}
        options={sourceOptions}
        isSearchable={false}
        isFullWidth
        isWithLoader={false}
        control={control}
        errors={errors}
        formState={formState}
      />

      {isDetailRequired(sourceCode) && (
        <Input
          name="source_detail"
          label={t(`signup-source-detail-${sourceCode}`)}
          placeholder={t(`signup-source-detail-${sourceCode}`)}
          isFullWidth
          errors={errors}
          formState={formState}
          ref={register}
        />
      )}

      <Consents>
        <Checkbox
          name="consent_terms"
          value="true"
          label={
            <Trans i18nKey="signup-consent-terms" t={t}>
              I have read and accept the
              <a
                href={FINTWIN_DOCS.websiteTerms}
                rel="noopener noreferrer"
                target="_blank"
              >
                Website Terms of Use
              </a>
              and the
              <a
                href={FINTWIN_DOCS.privacyPolicy}
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>
              .
            </Trans>
          }
          ref={register}
        />
        <Checkbox
          name="attestation_own_initiative"
          value="true"
          label={t('signup-attestation-own-initiative')}
          ref={register}
        />
        <Checkbox
          name="attestation_canada_notice"
          value="true"
          label={t('signup-attestation-canada-notice')}
          ref={register}
        />
      </Consents>
    </Container>
  )
}
